import { PivotFieldsAggridT } from 'business/definitions';
import {
    UseQueryResult,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';
import { EMPTY_COLUMNS } from 'routes/BudgetModule/components/BudgetProposal/Resource/hooks/settings';
import { FieldSettingsT } from 'routes/BudgetModule/config/types';
import { RQAPICallWithData } from 'store/APIClient';

export type BudgetUserSettings = {
    analyseDafOperatingGrid: { columns: PivotFieldsAggridT };
    analyseDafInvestmentGrid: { columns: PivotFieldsAggridT };
    analyseDafCommonGrid: { columns: PivotFieldsAggridT };
    allInscriptionsGrid: { columns: FieldSettingsT[] };
};

const EMPTY_SETTINGS: BudgetUserSettings = {
    analyseDafOperatingGrid: {
        columns: EMPTY_COLUMNS,
    },
    analyseDafInvestmentGrid: {
        columns: EMPTY_COLUMNS,
    },
    analyseDafCommonGrid: {
        columns: EMPTY_COLUMNS,
    },
    allInscriptionsGrid: {
        columns: [],
    },
};

const fetchBudgetUserSettings = async (): Promise<BudgetUserSettings> => {
    const response = await RQAPICallWithData<BudgetUserSettings>({
        endpoint: 'budget_user_settings',
        method: 'GET',
    });

    return response.content.data || {};
};

const CACHE_KEY = 'current_budget_user_settings_query';

/**
 * Returns the current user (budget) settings, or default empty settings,
 * along with the usual react-query response.
 *
 * @returns `{ budgetUserSettings, isLoading, error, isFetching, ... }`
 */
export const useGetBudgetUserSettings = (): {
    budgetUserSettings: BudgetUserSettings;
} & Omit<UseQueryResult<BudgetUserSettings, unknown>, 'data'> => {
    const { data, ...response } = useQuery({
        queryKey: [CACHE_KEY],
        queryFn: fetchBudgetUserSettings,
    });
    const budgetUserSettings = data ?? EMPTY_SETTINGS;

    return { budgetUserSettings, ...response };
};

/**
 * Build and returns the react-query mutation that patches the budget user settings.
 */
export const useMutateBudgetUserSettings = (): {
    mutate: (arg1: Partial<BudgetUserSettings>) => void;
} => {
    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationFn: (body: Partial<BudgetUserSettings>) =>
            RQAPICallWithData<BudgetUserSettings>({
                endpoint: 'budget_user_settings',
                method: 'PATCH',
                body,
            }),

        onSuccess: (response) => {
            queryClient.setQueryData(
                [CACHE_KEY],
                (oldData: BudgetUserSettings) => ({
                    ...oldData,
                    ...response.content.data,
                }),
            );
        },
    });
    return mutation;
};
